import { useEffect, useRef } from "react";
import L from "leaflet";
import { STATE_META, vehicleState } from "@/lib/fleet";

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

function markerHtml(vehicle, selected) {
  const state = vehicleState(vehicle);
  const meta = STATE_META[state];
  return `<div class="doot-marker ${meta.cls} ${selected ? "selected" : ""}">
    ${meta.cls === "moving" ? '<div class="ring"></div>' : ""}
    ${selected ? '<div class="halo"></div>' : ""}
    <div class="core" style="background:${meta.color}"></div>
    <div class="plate">${vehicle.plate_number}</div>
  </div>`;
}

export const FleetMap = ({ vehicles, selectedId, onSelect, geofences = [] }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const fencesRef = useRef([]);
  const fittedRef = useRef(false);

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [25.9, 93.7],
      zoom: 6,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer(DARK_TILES, { maxZoom: 19, attribution: "© OpenStreetMap, © CARTO" }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();

    vehicles.forEach((v) => {
      if (!v.current_location) return;
      seen.add(v.id);
      const pos = [v.current_location.lat, v.current_location.lng];
      const icon = L.divIcon({
        className: "",
        html: markerHtml(v, v.id === selectedId),
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      const existing = markersRef.current[v.id];
      if (existing) {
        existing.setLatLng(pos);
        existing.setIcon(icon);
      } else {
        const m = L.marker(pos, { icon, riseOnHover: true }).addTo(map);
        m.on("click", () => onSelect(v.id));
        markersRef.current[v.id] = m;
      }
    });

    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    if (!fittedRef.current && seen.size > 0) {
      const bounds = L.latLngBounds(
        vehicles.filter((v) => v.current_location).map((v) => [v.current_location.lat, v.current_location.lng])
      );
      map.fitBounds(bounds, { padding: [70, 70], maxZoom: 9 });
      fittedRef.current = true;
    }
  }, [vehicles, selectedId, onSelect]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    fencesRef.current.forEach((c) => map.removeLayer(c));
    fencesRef.current = geofences
      .filter((f) => f.is_active && f.center)
      .map((f) =>
        L.circle([f.center.lat, f.center.lng], {
          radius: f.radius_m || 500,
          color: "#00F0FF",
          weight: 1,
          fillColor: "#00F0FF",
          fillOpacity: 0.08,
        })
          .bindTooltip(f.name, { direction: "top" })
          .addTo(map)
      );
  }, [geofences]);

  useEffect(() => {
    const map = mapRef.current;
    const v = vehicles.find((x) => x.id === selectedId);
    if (map && v?.current_location) {
      map.flyTo([v.current_location.lat, v.current_location.lng], Math.max(map.getZoom(), 11), {
        duration: 0.7,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return <div ref={containerRef} data-testid="fleet-map" className="h-full w-full" />;
};
